import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Alert, Tab, Tabs } from 'react-bootstrap';
import { applicationService } from '../services/api';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { format } from 'date-fns';

const MyTripsPage = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMyApplications();
  }, []);

  const fetchMyApplications = async () => {
    try {
      setLoading(true);
      const response = await applicationService.getUserApplications();
      setApplications(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load your trip applications.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage message="Loading your trips..." />;
  }

  const pendingApps = applications.filter(app => app.status === 'pending');
  const approvedApps = applications.filter(app => app.status === 'approved');
  const rejectedApps = applications.filter(app => app.status === 'rejected');
  const waitingListApps = applications.filter(app => app.status === 'waitingList');

  const upcomingTrips = approvedApps.filter(app => 
    new Date(app.tripId?.startDate) > new Date()
  );

  const ApplicationCard = ({ app }) => {
    const trip = app.tripId || app.trip;
    if (!trip) return null;

    const statusColor = {
      pending: 'warning',
      approved: 'success',
      rejected: 'danger',
      waitingList: 'info'
    }[app.status];
    
    const statusLabel = {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      waitingList: 'Waiting List'
    }[app.status];

    return (
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h5>{trip.name}</h5>
              <p className="text-muted mb-2">
                <i className="bi bi-geo-alt-fill me-2"></i>
                {trip.location}
              </p>
              <p className="text-muted mb-2">
                <i className="bi bi-calendar-event me-2"></i>
                {format(new Date(trip.startDate), 'MMM d, yyyy')} - 
                {format(new Date(trip.endDate), 'MMM d, yyyy')}
              </p>
              {trip.price && (
                <p className="text-muted mb-2">
                  <i className="bi bi-currency-euro me-2"></i>
                  <strong>{trip.price} {trip.currency || 'EUR'}</strong>
                </p>
              )}
            </div>
            <div className="text-end">
              <Badge bg={statusColor} className="mb-2">
                {statusLabel}
              </Badge>
              {app.status === 'waitingList' && app.waitingListPosition && (
                <div className="mb-2">
                  <Badge bg="secondary" className="me-1">
                    Position #{app.waitingListPosition}
                  </Badge>
                </div>
              )}
              <div>
                <small className="text-muted">
                  Applied: {format(new Date(app.createdAt), 'MMM d, yyyy')}
                </small>
              </div>
            </div>
          </div>
          
          {app.adminComments && (
            <Alert variant="info" className="mt-3 mb-0">
              <strong>Admin message:</strong> {app.adminComments}
            </Alert>
          )}

          <div className="mt-3">
            <Link to={`/trips/${trip._id}`} className="btn btn-outline-primary btn-sm">
              View Trip Details
            </Link>
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Container>
      <h1 className="mb-4">My Trips</h1>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Quick Stats */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3 mb-md-0">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{applications.length}</h3>
              <p className="mb-0 text-muted">Total Applications</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3 mb-md-0">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">{approvedApps.length}</h3>
              <p className="mb-0 text-muted">Approved</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3 mb-md-0">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-warning">{pendingApps.length}</h3>
              <p className="mb-0 text-muted">Pending</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-info">{waitingListApps.length}</h3>
              <p className="mb-0 text-muted">Waiting List</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {applications.length === 0 ? (
        <Alert variant="info">
          <h5>No trip applications yet!</h5>
          <p>Browse our <Link to="/trips">available trips</Link> and start your diving adventure.</p>
        </Alert>
      ) : (
        <Tabs defaultActiveKey="all" className="mb-3">
          <Tab eventKey="all" title={`All (${applications.length})`}>
            {applications.map(app => (
              <ApplicationCard key={app._id} app={app} />
            ))}
          </Tab>

          <Tab eventKey="upcoming" title={`Upcoming (${upcomingTrips.length})`}>
            {upcomingTrips.length === 0 ? (
              <Alert variant="info">No upcoming trips.</Alert>
            ) : (
              upcomingTrips.map(app => (
                <ApplicationCard key={app._id} app={app} />
              ))
            )}
          </Tab>

          <Tab eventKey="pending" title={`Pending (${pendingApps.length})`}>
            {pendingApps.length === 0 ? (
              <Alert variant="info">No pending applications.</Alert>
            ) : (
              pendingApps.map(app => (
                <ApplicationCard key={app._id} app={app} />
              ))
            )}
          </Tab>

          <Tab eventKey="approved" title={`Approved (${approvedApps.length})`}>
            {approvedApps.length === 0 ? (
              <Alert variant="info">No approved applications yet.</Alert>
            ) : (
              approvedApps.map(app => (
                <ApplicationCard key={app._id} app={app} />
              ))
            )}
          </Tab>

          <Tab eventKey="rejected" title={`Rejected (${rejectedApps.length})`}>
            {rejectedApps.length === 0 ? (
              <Alert variant="info">No rejected applications.</Alert>
            ) : (
              rejectedApps.map(app => (
                <ApplicationCard key={app._id} app={app} />
              ))
            )}
          </Tab>

          <Tab eventKey="waitingList" title={`Waiting List (${waitingListApps.length})`}>
            {waitingListApps.length === 0 ? (
              <Alert variant="info">No waiting list applications.</Alert>
            ) : (
              <>
                <Alert variant="info" className="mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  You'll be automatically notified if a spot opens up in any of these trips.
                </Alert>
                {waitingListApps.map(app => (
                  <ApplicationCard key={app._id} app={app} />
                ))}
              </>
            )}
          </Tab>
        </Tabs>
      )}
    </Container>
  );
};

export default MyTripsPage;
